import { RenderSeedProps, Seed, SingleLineSeed } from "react-brackets";
import SingleMatch from "./MatchComponents/SingleMatch";
import EventBus from "react-native-event-bus";
import MatchStatus from "./MatchComponents/MatchStatus";
import VisibilitySensor from "react-visibility-sensor";
import { useState, useContext, useEffect, useRef } from "react";
import { TournamentMatchesContext } from "../Context/TournamentMatchesContext";
import GenerateBracketRounds from "../Hooks/GenerateBracketRounds";
import MatchResultService from "../../services/MatchResultService";
import EMatchStatus from "../../enums/MatchStatus";
import MatchLevel from "../../enums/MatchLevel";
import { EventsContext } from "../Context/EventsContext";
import MatchService from "../../services/MatchService";
import IMatch from "../Interfaces/Match";
import { useTranslation } from "react-i18next";

const viewDetails = (details: any) => {
  EventBus.getInstance().fireEvent("SHOW_MATCH_DETAILS", { ...details });
};

const CustomSeed = ({
  seed,
  breakpoint,
  roundIndex,
  seedIndex,
  isFullScreen = false,
  filteredRounds,
}: any) => {
  const [visible, setVisible] = useState(false);
  const { t } = useTranslation();
  const { tournamentMatches, matchCache } = useContext(
    TournamentMatchesContext
  );
  const rounds = filteredRounds
    ? filteredRounds
    : GenerateBracketRounds.generate(tournamentMatches?.tournament?.matches);
  const [multipleMatchStatus, setMultipleMatchStatus] = useState<number>(
    MatchResultService.getMultipleMatchStatus(tournamentMatches?.matches, seed)
  );
  const matchId = seed.matchesId[0];
  const [match, setMatch] = useState<IMatch | undefined>(
    matchCache.get(matchId)
  );

  const { socket, isConnected } = useContext(EventsContext);

  const selectedRound = rounds.winners.find(
    (round) => round.title === seed.round
  )
    ? rounds.winners
    : rounds.loosers;
  const isLineConnector =
    selectedRound[roundIndex]?.seeds.length ===
    selectedRound[roundIndex + 1]?.seeds.length;
  const inverti =
    seed?.matchLevel === MatchLevel.DOUBLE_BRACKET && !isLineConnector
      ? "skp-brackets-seed-left"
      : "";
  const Wrapper = isLineConnector ? SingleLineSeed : Seed;
  const multipleMatch = seed?.matchesId?.length > 1;
  const hasNext = !!selectedRound[roundIndex + 1];

  // 📡 Socket aggiornamento stato match
  useEffect(() => {
    if (isConnected && socket && matchId) {
      // 🔁 Handler per aggiornamento completo del match
      const updateHandler = (matchElement: IMatch) => {
        matchCache.set(matchId, matchElement);
        setMatch(matchElement);
        setMultipleMatchStatus(
          MatchResultService.getMultipleMatchStatusNoSeed([matchElement])
        );
      };

      // 🔄 Handler per richiesta esplicita di refresh
      const refreshHandler = () => {
        console.log("🟢 Messaggio ricevuto su", matchId);
        console.log(seed.matchesId);
        MatchService.getMatches(seed.matchesId)
          .then((Rmatches) => {
            console.log(Rmatches);
            setMultipleMatchStatus(
              MatchResultService.getMultipleMatchStatusNoSeed(Rmatches)
            );
            return MatchService.getSingleMatch(matchId);
          })
          .then((Rmatch) => {
            if (Rmatch) {
              matchCache.set(matchId, Rmatch);
              setMatch(Rmatch);
            }
          })
          .catch((error) => {
            console.error("Errore durante il recupero dei match:", error);
          });
      };

      socket.on(matchId, updateHandler);
      socket.on(`${matchId}-refresh`, refreshHandler);

      return () => {
        socket.off(matchId, updateHandler);
        socket.off(`${matchId}-refresh`, refreshHandler);
      };
    }
  }, [matchId, isConnected, socket]);

  // 📦 Caricamento iniziale del match
  useEffect(() => {
    if (matchId && visible && !match) {
      if (matchCache.has(matchId)) {
        setMatch(matchCache.get(matchId));
      } else {
        MatchService.getSingleMatch(matchId).then((Rmatch) => {
          if (Rmatch) {
            matchCache.set(matchId, Rmatch);
            setMatch(Rmatch);
          }
        });
      }
    }
  }, [matchId, visible, match]);

  return (
    <VisibilitySensor
      partialVisibility
      offset={{ top: 10, left: 10 }}
      delayedCall
      onChange={(isVisible: boolean) => {
        setVisible(isVisible);
      }}
    >
      <Wrapper
        mobileBreakpoint={breakpoint}
        style={{ fontSize: 12 }}
        className={
          !isFullScreen
            ? hasNext
              ? `skp-brackets-seed ${inverti}`
              : `skp-brackets-seed-hide-line ${inverti}`
            : `${inverti}`
        }
      >
        {!isFullScreen && (
          <div
            className="skp-text-label float-left mb-1 skp-text-light skp-link-primary"
            id={`seed-${seed.id}`}
            onClick={() => viewDetails(seed)}
          >
            Match {seed.id}
          </div>
        )}
        {isFullScreen && (
          <div
            className="skp-text-label float-left skp-link-primary"
            id={`seed-${seed.id}`}
            onClick={() => viewDetails(seed)}
            style={{ position: "absolute", left: "5px", marginBottom: "15px" }}
          >
            {seed?.matchesId?.length === 1 && (
              <span className={`color-${match?.status}`}>{seed.id}</span>
            )}
            {multipleMatch && (
              <span className={`color-${multipleMatchStatus}`}>{seed.id}</span>
            )}
          </div>
        )}

        <SingleMatch
          matchId={matchId}
          seed={seed}
          visible={visible}
          match={match}
        />

        {!isFullScreen && (
          <div className="columns col-gapless skp-brackets-seed-details skp-text-label">
            <div
              className="skp-link-primary column col-5 col-gapless text-left skp-text-light"
              onClick={() => viewDetails(seed)}
            >
              {seed?.matchesId?.length === 1 && (
                <div className="skp-brackets-seed-status text-left mb-1">
                  <span className={`skp-chip status-${match?.status}`}>
                    {match && t(EMatchStatus[match.status])}
                  </span>
                </div>
              )}
              {multipleMatch && (
                <div className="skp-brackets-seed-status text-left mb-1">
                  <span className={`skp-chip status-${multipleMatchStatus}`}>
                    {t(EMatchStatus[multipleMatchStatus])}
                  </span>
                </div>
              )}
            </div>

            <div className="column col-7 text-right">
              <span
                className="skp-link-primary"
                onClick={() => viewDetails(seed)}
              >
                {t("matches.enterMatch")}
              </span>
            </div>
          </div>
        )}
      </Wrapper>
    </VisibilitySensor>
  );
};

export default CustomSeed;
